import { Trans, useTranslation } from 'react-i18next';
import { AdjustmentItem } from 'models/order/AdjustmentItem';
import useOpenOrderAdjustmentModal from 'components/domain/order/useOpenOrderAdjustmentModal';
import { queryClient } from 'queryClient';
import useAppModal from 'hooks/useAppModal';
import { LEAD_GQL_QUERY_BASE } from 'components/domain/lead/useFetchLead';
import { TextHighlightGreen } from 'components/text/TextHighlightGreen';
import useFormatCurrency from 'hooks/useFormatCurrency';
import useDeleteOrderAdjustment from 'components/domain/orderAdjustment/useDeleteOrderAdjustment';
import { HasOrderItems } from 'models/hasOrderItems/HasOrderItems';
import { Lead } from 'models/Leads';
import { PriceDetailsList } from '../OrderViewPriceDetails.styles';
import OrderViewPriceDetailsListItem from '../OrderViewPriceDetailsListItem';
import { PriceDetailsItem } from '../OrderViewPriceDetailsListItemRow';

const AdjustmentItems = ({
  lead,
  order,
  handleOrderAdjustment,
}: {
  lead: Lead;
  order: HasOrderItems;
  handleOrderAdjustment: boolean;
}) => {
  const { uid: orderUid, adjustmentItems, currency } = order;
  const { uid: leadUid } = lead;

  const { openConfirmModal } = useAppModal();
  const { t } = useTranslation();
  const { updateOrderAdjustmentModal } = useOpenOrderAdjustmentModal();
  // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const { deleteOrderAdjustment } = useDeleteOrderAdjustment({ orderUid });
  const formatCurrency = useFormatCurrency();

  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const getAdjustmentItems = (otherAdjustments): PriceDetailsItem[] => {
    return otherAdjustments.map((adjustment: AdjustmentItem) => {
      const { adjustmentType, amount, uid, note } = adjustment;

      const adjustmentLabel = t(
        `componentOrder.adjustmentType.${adjustmentType}`,
      );

      return {
        label: note ? `${adjustmentLabel} - ${note}` : adjustmentLabel,
        amount,
        currency,
        onEdit: () =>
          updateOrderAdjustmentModal({
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            orderUid,
            leadUid,
            currency,
            adjustmentItem: adjustment,
          }),
        onDelete: () =>
          openConfirmModal({
            title: t('componentOrderAdjustment.deleteModal.title'),
            body: (
              <Trans
                i18nKey="componentOrderAdjustment.deleteModal.body"
                values={{
                  adjustmentType: t(
                    `componentOrderAdjustment.adjustmentType.${adjustmentType}`,
                  ),
                  adjustmentAmount: formatCurrency({
                    value: amount,
                    currency,
                  }),
                }}
                components={{ highlight: <TextHighlightGreen /> }}
              />
            ),
            confirmColor: 'danger',
            onConfirm: () => {
              deleteOrderAdjustment({
                uid,
                successCallback: () => {
                  queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]);
                },
              });
            },
          }),
      };
    });
  };

  return (
    <PriceDetailsList data-testid="price-details-list-adjustment-items">
      <OrderViewPriceDetailsListItem
        key="adjustments-total-amount"
        title={t('componentOrder.adjustments')}
        priceDetailsItems={[
          ...(handleOrderAdjustment ? getAdjustmentItems(adjustmentItems) : []),
          {
            label: t('componentOrder.totalAdjustments'),
            // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            amount: adjustmentItems.reduce(
              (acc, adjustmentItem) => acc + adjustmentItem.amount,
              0,
            ),
            isTotalAmount: true,
            currency,
          },
        ]}
      />
    </PriceDetailsList>
  );
};

export default AdjustmentItems;
